import { z } from "zod";

// TES-928 — cookieless, server-side visit statistics for the public web.
//
// The web's Astro middleware records one page view per HTML page it renders
// and posts it to the API over the internal network. Nothing here identifies
// a person: there is no IP address, no user agent and no query string. The
// visitor hash only groups views from the same visitor on the same day,
// because its salt is discarded when the day changes.

export const PAGE_VIEW_DEVICES = ["mobile", "tablet", "desktop"] as const;
export const pageViewDeviceSchema = z.enum(PAGE_VIEW_DEVICES);
export type PageViewDevice = (typeof PAGE_VIEW_DEVICES)[number];

const shortText = (max: number) => z.string().trim().min(1).max(max);

export const pageViewIngestSchema = z.object({
  /** Route pattern (e.g. `/app/[...path]`), or an article's pathname. */
  path: shortText(200).regex(/^\/[^?#\s]*$/, "path must not carry a query"),
  locale: z.enum(["sv", "en"]),
  device: pageViewDeviceSchema,
  referrerHost: shortText(253).nullable(),
  utmSource: shortText(100).nullable(),
  utmMedium: shortText(100).nullable(),
  utmCampaign: shortText(100).nullable(),
  /** Truncated sha256 hex of (daily salt + IP + user agent). */
  visitorHash: z.string().regex(/^[0-9a-f]{16,64}$/),
});
export type PageViewIngest = z.infer<typeof pageViewIngestSchema>;

/**
 * The steps of the purchase funnel, as the paths the report folds together.
 * The English variant of each page counts as the same step.
 */
export const WEB_FUNNEL_STEPS = [
  { key: "home", paths: ["/", "/en"] },
  { key: "bmi", paths: ["/bmi", "/en/bmi"] },
  { key: "subscribe", paths: ["/subscribe", "/en/subscribe"] },
  { key: "checkout", paths: ["/checkout", "/en/checkout"] },
  {
    key: "checkout_success",
    paths: ["/checkout/success", "/en/checkout/success"],
  },
] as const;
export type WebFunnelStepKey = (typeof WEB_FUNNEL_STEPS)[number]["key"];

const count = z.number().int().nonnegative();
const labelledCount = z.object({ label: z.string(), views: count });

export const webAnalyticsReportSchema = z.object({
  /** Inclusive UTC days, YYYY-MM-DD. */
  from: z.string(),
  to: z.string(),
  totals: z.object({
    views: count,
    /** Sum of daily uniques: a visitor who returns on two days counts twice. */
    visitorDays: count,
  }),
  daily: z.array(
    z.object({ day: z.string(), views: count, visitors: count }),
  ),
  topPaths: z.array(
    z.object({ path: z.string(), views: count, visitors: count }),
  ),
  referrers: z.array(labelledCount),
  devices: z.array(labelledCount),
  campaigns: z.array(
    z.object({
      source: z.string().nullable(),
      medium: z.string().nullable(),
      campaign: z.string().nullable(),
      views: count,
    }),
  ),
  /**
   * Same-day funnel: distinct (day, visitor) pairs that viewed each step.
   * Not strictly sequential — a visitor who lands directly on /subscribe
   * counts there without having counted at /.
   */
  funnel: z.array(
    z.object({
      step: z.enum(
        WEB_FUNNEL_STEPS.map((s) => s.key) as [
          WebFunnelStepKey,
          ...WebFunnelStepKey[],
        ],
      ),
      visitorDays: count,
    }),
  ),
});
export type WebAnalyticsReport = z.infer<typeof webAnalyticsReportSchema>;
